import axios from 'axios';

/**
 * Platform Admin Service
 *
 * Wraps the `/api/platform/*` endpoints used by Funnelflow super admins.
 * Backend enforces real auth (`requirePlatformAdmin`); frontend route guards
 * only hide the UI from non-admins.
 */
export const platformAdminService = {
  // ============================================
  // ORGANIZATIONS
  // ============================================

  /**
   * Cross-tenant list of organizations (with member-count, base license, modules).
   * @param {{ search?: string, status?: string, limit?: number, offset?: number }} params
   * @returns {Promise<Object>} { organizations, pagination }
   */
  async getOrganizations(params = {}) {
    const response = await axios.get('/platform/organizations', { params });
    return response.data;
  },

  /**
   * Full organization detail (members, subscriptions, counts).
   */
  async getOrganization(organizationId) {
    const response = await axios.get(`/platform/organizations/${organizationId}`);
    return response.data;
  },

  /**
   * Update org status (active / suspended / archived).
   */
  async updateOrganizationStatus(organizationId, status) {
    const response = await axios.patch(`/platform/organizations/${organizationId}/status`, {
      status,
    });
    return response.data;
  },

  /**
   * Turn sales orders on or off for an organization.
   */
  async setSalesOrders(organizationId, enabled) {
    const response = await axios.patch(`/platform/organizations/${organizationId}/sales-orders`, {
      enabled,
    });
    return response.data;
  },

  /**
   * Create a new organization and invite the first owner.
   * @param {{ name: string, slug: string, ownerEmail: string, ownerRole?: string }} payload
   */
  async createOrganization(payload) {
    const response = await axios.post('/platform/organizations', payload);
    return response.data;
  },

  /**
   * Remove a member from an organization (keeps the user account + other orgs).
   * The last admin and the last real member are hard-blocked by the backend.
   */
  async removeOrganizationMember(organizationId, userId) {
    const response = await axios.delete(
      `/platform/organizations/${organizationId}/members/${userId}`
    );
    return response.data;
  },

  async getOnboardingStatus(organizationId) {
    const response = await axios.get(`/platform/organizations/${organizationId}/onboarding`);
    return response.data;
  },

  async disableOnboarding(organizationId) {
    const response = await axios.post(
      `/platform/organizations/${organizationId}/onboarding/disable`
    );
    return response.data;
  },

  async enableOnboarding(organizationId) {
    const response = await axios.post(
      `/platform/organizations/${organizationId}/onboarding/enable`
    );
    return response.data;
  },

  async resetOnboarding(organizationId) {
    const response = await axios.post(`/platform/organizations/${organizationId}/onboarding/reset`);
    return response.data;
  },

  // ============================================
  // BILLING
  // ============================================

  /**
   * Same shape as getOrganizations but with billing-summary fields.
   * Used for the org-picker in the billing tools.
   */
  async getBillingOrganizations(params = {}) {
    const response = await axios.get('/platform/billing/organizations', { params });
    return response.data;
  },

  /**
   * List of all active licenses (base + modules) — for the license picker.
   */
  async getLicenses() {
    const response = await axios.get('/platform/billing/licenses');
    return response.data;
  },

  /**
   * Generate a Stripe Checkout link with optional trial / overrides.
   */
  async createCheckoutLink(payload) {
    const response = await axios.post('/platform/billing/checkout-link', payload);
    return response.data;
  },

  /**
   * Extend the trial of an active Stripe subscription.
   */
  async extendTrial({ organizationId, trialEndDate }) {
    const response = await axios.post('/platform/billing/trial/extend', {
      organizationId,
      trialEndDate,
    });
    return response.data;
  },

  // ============================================
  // PRODUCTS
  // ============================================

  /**
   * Read-only catalog of the source org — used to power the selection UI.
   * Capped at 2000 products server-side; pass `search` to filter by title or
   * base_sku for larger catalogs.
   * @param {{ organizationId: string, search?: string }} params
   * @returns {Promise<{ feeds: Array, categories: Array, products: Array }>}
   */
  async getProductsSourceCatalog({ organizationId, search } = {}) {
    const response = await axios.get('/platform/products/source-catalog', {
      params: {
        organizationId,
        ...(search ? { search } : {}),
      },
    });
    return response.data;
  },

  /**
   * Copy products + categories + feeds + upsell-remap from one org to another.
   *
   * If none of `productIds` / `feedIds` / `categoryIds` are provided the full
   * catalog is copied (backward-compatible). When provided, the three arrays
   * are unioned: every product in `productIds` ∪ feeds ∪ categories.
   *
   * @param {{
   *   sourceOrganizationId: string,
   *   targetOrganizationId: string,
   *   dryRun?: boolean,
   *   productIds?: string[],
   *   feedIds?: string[],
   *   categoryIds?: string[],
   * }} payload
   */
  async copyProducts(payload) {
    const response = await axios.post('/platform/products/copy', payload);
    return response.data;
  },

  /**
   * Seed or re-seed an organization from a source tenant.
   * Omit `targetOrganizationId` to use the shared demo org.
   */
  async seedDemoOrg(payload) {
    const response = await axios.post('/platform/demo/seed', payload);
    return response.data;
  },

  // ============================================
  // USERS / IMPERSONATION
  // ============================================

  /**
   * Cross-org user search. Accepts e.g. `{ search: 'cas@' }`.
   */
  async searchUsers(params = {}) {
    const response = await axios.get('/platform/users', { params });
    return response.data;
  },

  /**
   * Full detail of one user (incl. all org memberships).
   */
  async getUser(userId) {
    const response = await axios.get(`/platform/users/${userId}`);
    return response.data;
  },

  /**
   * Generate a magic link to log in as another user.
   * Frontend MUST persist the admin's session before redirecting (see useImpersonation).
   */
  async startImpersonation(userId, redirectTo = '/') {
    const response = await axios.post(`/platform/users/${userId}/impersonate`, {
      redirectTo,
    });
    return response.data;
  },

  /**
   * Audit-log the end of an impersonation session. Always called from the
   * "exit impersonation" UI before the admin session is restored.
   */
  async endImpersonation(targetUserId) {
    const response = await axios.post('/platform/users/impersonate/end', {
      targetUserId,
    });
    return response.data;
  },

  // ============================================
  // LICENSES (manual / quantity / cancel)
  // ============================================

  /**
   * Grant a manual (non-Stripe) license.
   * Fails with 409 if an active subscription already exists for that license.
   */
  async grantManualLicense(payload) {
    const response = await axios.post('/platform/licenses/manual', payload);
    return response.data;
  },

  /**
   * Update seat-count for an active subscription (works for Stripe + manual).
   */
  async updateLicenseQuantity({ organizationId, quantity }) {
    const response = await axios.patch('/platform/licenses/quantity', {
      organizationId,
      quantity,
    });
    return response.data;
  },

  /**
   * Cancel a subscription. Cancelling a base license cascades to its modules.
   */
  async cancelLicense(subscriptionId, organizationId) {
    const response = await axios.post(`/platform/licenses/${subscriptionId}/cancel`, {
      organizationId,
    });
    return response.data;
  },

  // ============================================
  // AUDIT LOG
  // ============================================

  /**
   * Read-only audit log viewer.
   * @param {{
   *   actor_user_id?: string,
   *   action?: string,
   *   target_organization_id?: string,
   *   status?: 'success'|'error'|'dry_run',
   *   from?: string,
   *   to?: string,
   *   limit?: number,
   *   offset?: number,
   * }} params
   */
  async getAuditLog(params = {}) {
    const response = await axios.get('/platform/audit', { params });
    return response.data;
  },

  // ============================================
  // STATS / LANDING
  // ============================================

  /**
   * Combined payload for the platform admin landing page.
   * Returns `{ kpis, attention, recent_activity, generated_at }`.
   */
  async getStats() {
    const response = await axios.get('/platform/stats');
    return response.data;
  },

  /**
   * KPIs only — useful for refresh-button on the landing page.
   */
  async getStatsKpis() {
    const response = await axios.get('/platform/stats/kpis');
    return response.data;
  },

  /**
   * Action items only — designed for cheap polling (every ~60s).
   */
  async getStatsAttention() {
    const response = await axios.get('/platform/stats/attention');
    return response.data;
  },

  /**
   * Recent admin activity feed — designed for cheap polling (every ~30s).
   */
  async getStatsRecentActivity(limit = 10) {
    const response = await axios.get('/platform/stats/recent-activity', {
      params: { limit },
    });
    return response.data;
  },

  /**
   * MRR / customers timeline used to power the platform admin chart.
   * Returns `weeks + 1` data points (oldest → newest, last point = today).
   *
   * @param {{ weeks?: number, interval?: 'weekly'|'monthly' }} params
   * @returns {Promise<{ interval: string, weeks: number, points: Array }>}
   */
  async getStatsTimeline({ weeks = 12, interval = 'weekly' } = {}) {
    const response = await axios.get('/platform/stats/timeline', {
      params: { weeks, interval },
    });
    return response.data;
  },

  async getCatalogSuppliers() {
    const response = await axios.get('/platform/catalog/suppliers');
    return response.data;
  },

  async createCatalogSupplier({ name }) {
    const response = await axios.post('/platform/catalog/suppliers', { name });
    return response.data;
  },

  async getCatalogMasters({ withStatus = false } = {}) {
    const response = await axios.get('/platform/catalog/masters', {
      params: withStatus ? { with_status: true } : {},
    });
    return response.data;
  },

  async createCatalogMaster({ catalogSupplierId, title, category }) {
    const response = await axios.post('/platform/catalog/masters', {
      catalog_supplier_id: catalogSupplierId,
      title,
      category: category || null,
    });
    return response.data;
  },

  async getCatalogMaster(masterId) {
    const response = await axios.get(`/platform/catalog/masters/${masterId}`);
    return response.data;
  },

  async saveCatalogDraft(masterId, draft) {
    const response = await axios.patch(`/platform/catalog/masters/${masterId}`, { draft });
    return response.data;
  },

  async scaleCatalogPrices(masterId, percentage, { dryRun = false } = {}) {
    const response = await axios.post(`/platform/catalog/masters/${masterId}/scale-prices`, {
      percentage,
      dry_run: dryRun,
    });
    return response.data;
  },

  async getCatalogUpsellCandidates() {
    const response = await axios.get('/platform/catalog/masters', {
      params: { summary: 'upsell' },
    });
    return response.data;
  },

  async publishCatalogMaster(masterId, { effectiveFrom } = {}) {
    const response = await axios.post(`/platform/catalog/masters/${masterId}/publish`, {
      effective_from: effectiveFrom || null,
    });
    return response.data;
  },

  async getCatalogSetMasters(setId) {
    const response = await axios.get(`/platform/catalog/sets/${setId}/masters`);
    return response.data;
  },

  async publishCatalogSetMasters(setId, { masterIds, effectiveFrom }) {
    const response = await axios.post(`/platform/catalog/sets/${setId}/publish`, {
      master_ids: masterIds,
      effective_from: effectiveFrom || null,
    });
    return response.data;
  },

  async getCatalogSets(catalogSupplierId, { withStatus = false } = {}) {
    const response = await axios.get('/platform/catalog/sets', {
      params: {
        ...(catalogSupplierId ? { catalog_supplier_id: catalogSupplierId } : {}),
        ...(withStatus ? { with_status: true } : {}),
      },
    });
    return response.data;
  },

  async createCatalogSet({ name, description, catalogSupplierId, values }) {
    const response = await axios.post('/platform/catalog/sets', {
      name,
      description,
      catalog_supplier_id: catalogSupplierId || null,
      values,
    });
    return response.data;
  },

  async updateCatalogSet(setId, { name, description, catalogSupplierId }) {
    const response = await axios.patch(`/platform/catalog/sets/${setId}`, {
      name,
      description,
      catalog_supplier_id: catalogSupplierId,
    });
    return response.data;
  },

  async saveCatalogSetValues(setId, values) {
    const response = await axios.put(`/platform/catalog/sets/${setId}/values`, { values });
    return response.data;
  },
};

export default platformAdminService;
